<script setup>
// ============================================================================
// BonBoZ: "Tổng quan về hoạt động" — cột + HAI đường, dựng theo Pancake.
//
// Khác `BieuDoCotDuong.vue` (cột + một đường) ở chỗ có ba chuỗi, và ba chuỗi
// này lệch thang rất xa: tương tác đếm bằng trăm, khách mới bằng chục, đơn chốt
// bằng đơn vị. Nên hai đường DÙNG CHUNG trục phải, cột đi trục trái — đúng như
// Pancake, và cũng là cách duy nhất để đường đơn chốt không nằm bẹp dưới đáy.
//
// Ba việc giảm đọc sai của `BieuDoCotDuong.vue` vẫn giữ nguyên ở đây: tên trục
// tô đúng màu chuỗi dùng nó, hai HÌNH khác hẳn nhau (cột đặc / đường có điểm),
// và chú giải ghi rõ từng chuỗi. Chỗ hai đường cắt nhau vẫn không mang nghĩa gì
// — kéo giãn một trục là đổi được chỗ cắt tuỳ ý.
//
// ĐĂNG KÝ Ở THÂN MODULE, không phải trong `onMounted`: `onMounted` của component
// CHA chạy SAU của con, mà `<Chart>` dựng biểu đồ ngay trong `onMounted` của
// nó — lúc đó controller chưa đăng ký thì canvas trắng vĩnh viễn. Đã dính một
// lần ở `BieuDoTron.vue`.
// ============================================================================
import { computed } from 'vue';
import { Chart } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Tooltip,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
} from 'chart.js';
import { mauCss } from 'dashboard/helper/mauBieuDo';

const props = defineProps({
  nhan: { type: Array, default: () => [] },
  tuongTac: { type: Array, default: () => [] },
  khachMoi: { type: Array, default: () => [] },
  donChot: { type: Array, default: () => [] },
  tenTuongTac: { type: String, default: '' },
  tenKhachMoi: { type: String, default: '' },
  tenDonChot: { type: String, default: '' },
});

ChartJS.register(
  Tooltip,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
);

// Đường mảnh, bo mềm, KHÔNG tô nền: hai đường chồng lên cột, tô nền là che mất
// cột bên dưới.
const duong = (nhan, so, bien) => ({
  type: 'line',
  label: nhan,
  yAxisID: 'yDuong',
  data: so,
  borderColor: () => mauCss(bien),
  backgroundColor: () => mauCss(bien),
  pointBackgroundColor: () => mauCss(bien),
  borderWidth: 2,
  pointRadius: 0,
  pointHoverRadius: 5,
  tension: 0.4,
  fill: false,
  order: 1,
});

const duLieu = computed(() => ({
  labels: props.nhan,
  datasets: [
    {
      type: 'bar',
      label: props.tenTuongTac,
      yAxisID: 'yCot',
      data: props.tuongTac,
      backgroundColor: () => mauCss('--bonboz-xong', 0.42),
      hoverBackgroundColor: () => mauCss('--bonboz-xong', 0.7),
      borderRadius: 3,
      borderSkipped: false,
      maxBarThickness: 18,
      order: 2,
    },
    duong(props.tenKhachMoi, props.khachMoi, '--bonboz-tre'),
    duong(props.tenDonChot, props.donChot, '--bonboz-mo'),
  ],
}));

const tuyChon = computed(() => ({
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    // Chú giải do trang cha vẽ bằng HTML — dễ dịch và style hơn legend của
    // chart.js, và đặt được đúng chỗ Pancake đặt (giữa, dưới khung).
    legend: { display: false },
    tooltip: {
      callbacks: { label: c => ` ${c.dataset.label}: ${c.formattedValue}` },
    },
  },
  scales: {
    x: {
      ticks: { maxRotation: 0, autoSkipPadding: 16, font: { size: 11 } },
      grid: { display: false },
      border: { display: false },
    },
    yCot: {
      position: 'left',
      beginAtZero: true,
      ticks: {
        precision: 0,
        maxTicksLimit: 4,
        color: () => mauCss('--bonboz-xong'),
        font: { size: 11 },
      },
      grid: { drawTicks: false },
      border: { display: false },
      title: {
        display: true,
        text: props.tenTuongTac,
        color: () => mauCss('--bonboz-xong'),
        font: { size: 10 },
      },
    },
    yDuong: {
      position: 'right',
      beginAtZero: true,
      ticks: { precision: 0, maxTicksLimit: 4, font: { size: 11 } },
      // Lưới chỉ vẽ theo trục TRÁI. Hai bộ lưới chồng nhau là thứ làm biểu đồ
      // hai trục rối nhất.
      grid: { drawOnChartArea: false },
      border: { display: false },
      title: {
        display: true,
        // Hai chuỗi dùng chung trục này nên tên trục phải nói cả hai, và để
        // màu trung tính — tô theo một chuỗi là ngầm bảo trục thuộc chuỗi đó.
        text: `${props.tenKhachMoi} / ${props.tenDonChot}`,
        font: { size: 10 },
      },
    },
  },
}));
</script>

<template>
  <Chart type="bar" :data="duLieu" :options="tuyChon" />
</template>
